import {
    CardStyleInterpolators,
    StackCardInterpolatedStyle,
    StackCardInterpolationProps,
} from "@react-navigation/stack";
import { useAtomValue } from "jotai";
import { useIsScreenSizeMin } from "./useIsScreenSizeMin";
import { useCallback } from "react";
import { navModalStackAtom } from "../atoms/modal";
import { STACK_ENTER_TRANSLATE_Y } from "../utils/modal-stack-slot";

// Canonical web modal animation. On large (desktop web) every modal — navigation modal screens,
// CreateMissionStack, and the JS `Modal` (components/ModalPortal.tsx) — animates identically with a
// dimming backdrop. The FIRST modal (opening over the app) enters with a fade + subtle scale
// (0.96 → 1); a STACKED modal (2nd and beyond) enters/exits with a small upward slide instead (rises
// on open, descends on close). This interpolator is the react-navigation half; ModalPortal's ModalCell
// is the reanimated half — keep them in sync. On small (mobile web) modals stay full-screen.
export const useModalCardStyleInterpolator = () => {
    const isLarge = useIsScreenSizeMin("large");
    // The top navigation modal is stacked once there is more than one (nav modals sit below JS modals,
    // so a 2nd nav modal always stacks on the first). Drives slide-vs-scale for its own entrance.
    const stacked = useAtomValue(navModalStackAtom).length > 1;

    const cardStyleInterpolator = useCallback<
        (props: StackCardInterpolationProps) => StackCardInterpolatedStyle
    >(
        (props) => {
            // Reuse the base for container/overlay/shadow sizing, but replace the card motion.
            const {
                containerStyle = {},
                overlayStyle = {},
                shadowStyle = {},
            } = CardStyleInterpolators.forFadeFromBottomAndroid(props) as {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                containerStyle: Record<string, any>;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                overlayStyle: Record<string, any>;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                shadowStyle: Record<string, any>;
            };

            const opacity = props.current.progress.interpolate({
                inputRange: [0, 1],
                outputRange: [0, 1],
                extrapolate: "clamp",
            });

            const backgroundColor = props.current.progress.interpolate({
                inputRange: [0, 1],
                outputRange: ["rgba(0, 0, 0, 0.0)", "rgba(0, 0, 0, 0.4)"],
                extrapolate: "clamp",
            });

            // Stacked modals slide (rise on open / descend on close); the first modal scales.
            const transform = stacked
                ? [
                      {
                          translateY: props.current.progress.interpolate({
                              inputRange: [0, 1],
                              outputRange: [STACK_ENTER_TRANSLATE_Y, 0],
                              extrapolate: "clamp",
                          }),
                      },
                  ]
                : [
                      {
                          scale: props.current.progress.interpolate({
                              inputRange: [0, 1],
                              outputRange: [0.96, 1],
                              extrapolate: "clamp",
                          }),
                      },
                  ];

            // IMPORTANT: only animate on `current.progress` (this card's own entrance), never on
            // `next.progress`. React Navigation makes a non-top card borrow the TOP screen's
            // cardStyleInterpolator (CardStack: "when a screen is not the last, it should use next
            // screen's transition config"), so the base app behind a modal is styled by THIS
            // interpolator. Reacting to `next` would move/scale the whole app when a modal opens.
            // A modal receding under another modal is handled by useModalRecedeStyle on the card itself.
            return {
                containerStyle: {
                    ...containerStyle,
                    backgroundColor,
                    overflow: "hidden",
                },
                cardStyle: {
                    opacity,
                    transform,
                },
                overlayStyle: { ...overlayStyle, opacity: 0 },
                shadowStyle: { ...shadowStyle },
            };
        },
        [stacked],
    );

    return !isLarge ? CardStyleInterpolators.forVerticalIOS : cardStyleInterpolator;
};
